import React, { useState, useEffect, useCallback } from 'react';
import AuthPage from './components/AuthPage';
import BudgetWidget from './components/BudgetWidget';
import SubscriptionPanel from './components/SubscriptionPanel';
import MagiTracker from './components/MagiTracker';
import AccountsSidebar from './components/AccountsSidebar';
import SpendingChart from './components/SpendingChart';
import RecentTransactions from './components/RecentTransactions';
import TopSpendersSnapshot from './components/TopSpendersSnapshot';
import BigPurchasesBanner from './components/BigPurchasesBanner';
import BigPurchasesSnapshot from './components/BigPurchasesSnapshot';
import AllTransactions from './components/AllTransactions';
import PlannedExpenses from './components/PlannedExpenses';
import Investments from './components/Investments';
import RealEstate from './components/RealEstate';
import LogoMark from './components/LogoMark';
import SecuritySettings from './components/SecuritySettings';
import BudgetSettings from './components/BudgetSettings';
import HouseholdMembers from './components/HouseholdMembers';
import CookieConsent from './components/CookieConsent';
import SponsorCard from './components/SponsorCard';
import SponsorTicker from './components/SponsorTicker';
import AdminPanel from './components/AdminPanel';
import HouseholdIncome from './components/HouseholdIncome';
import InvestmentGoal from './components/InvestmentGoal';
import BudgetCalculator from './components/BudgetCalculator';
import CategoryBudgets from './components/CategoryBudgets';
import FixedCostCategories from './components/FixedCostCategories';
import CategoryBudgetsSnapshot from './components/CategoryBudgetsSnapshot';
import SavingsGoals from './components/SavingsGoals';
import SafeToSpendHero from './components/SafeToSpendHero';
import DebtPayoff from './components/DebtPayoff';
import NeedsAttention from './components/NeedsAttention';
import StuckPendingReview from './components/StuckPendingReview';
import TransferReviewList from './components/TransferReviewList';
import FeedbackButton from './components/FeedbackButton';
import AIChat from './components/AIChat';
import SpendingBreakdownCard from './components/SpendingBreakdownCard';
import SpendingRecapCard from './components/SpendingRecapCard';
import BillsVsSpendable from './components/BillsVsSpendable';
import BudgetGlance from './components/BudgetGlance';
import InvestmentsSnapshot from './components/InvestmentsSnapshot';
import TopTickers from './components/TopTickers';
import IncomeTracker from './components/IncomeTracker';
import SubscriptionsSnapshot from './components/SubscriptionsSnapshot';
import MagiSnapshot from './components/MagiSnapshot';
import OnboardingWizard from './components/OnboardingWizard';
import InstallPrompt from './components/InstallPrompt';
import TwoFactorGate from './components/TwoFactorGate';
import { useDashboard } from './hooks/useDashboard';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import { api } from './utils/api';
import { initAnalyticsIfConsented, trackPageview } from './utils/analytics';

export default function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    initAnalyticsIfConsented();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('findash_token');
    if (token) {
      api.me()
        .then((u) => setUser(u))
        .catch(() => localStorage.removeItem('findash_token'))
        .finally(() => setAuthChecked(true));
    } else {
      setAuthChecked(true);
    }
  }, []);

  const handleLogin = (u, isNewSignup) => {
    setUser(u);
    if (isNewSignup) setShowOnboarding(true);
  };

  if (!authChecked) return <><LoadingScreen /><CookieConsent /></>;
  if (!user) return <><AuthPage onLogin={handleLogin} /><CookieConsent /></>;
  if (showOnboarding) return <OnboardingWizard onFinish={() => setShowOnboarding(false)} />;
  if (user.shouldPrompt2FA) {
    return (
      <TwoFactorGate
        onDone={async () => {
          try {
            const fresh = await api.me();
            setUser(fresh);
          } catch (err) {
            console.error(err);
          }
        }}
      />
    );
  }

  return <Dashboard user={user} setUser={setUser} onLogout={() => { localStorage.removeItem('findash_token'); setUser(null); }} />;
}

function ManageGroupHeader({ icon, title, accent = '#00d4aa' }) {
  return (
    <div style={styles.manageGroupHeader}>
      <div style={{ ...styles.manageGroupIconBadge, background: `${accent}22`, color: accent }}>
        {icon}
      </div>
      <span style={styles.manageGroupTitle}>{title}</span>
    </div>
  );
}

function Dashboard({ user, setUser, onLogout }) {
  useIdleTimeout(onLogout);

  useEffect(() => {
    api.trackEvent('login').catch(() => {}); // fire-and-forget, once per session
  }, []);

  const refreshUser = async () => {
    try {
      const u = await api.me();
      setUser(u);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  const {
    summary,
    subscriptions,
    magi,
    accounts,
    monthlySpend,
    plannedExpenses,
    loading,
    syncing,
    error,
    lastRefresh,
    refresh,
    sync,
  } = useDashboard();

  // Single unified refresh signal. A change anywhere (a saved budget, a new
  // income entry, a category override, a goal update...) can affect numbers
  // shown in many different places across the app - the budget widgets,
  // Income Tracker, category snapshots, MAGI, investments, all of it. Rather
  // than wiring each writer to know exactly which readers it affects (easy
  // to miss one), every save calls this ONE function, which refreshes the
  // central dashboard data, the user/settings data, and bumps refreshKey -
  // every self-fetching widget below includes refreshKey in its own fetch
  // effect, so a bump makes them all refetch too.
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshAll = useCallback(async () => {
    await Promise.all([refresh(), refreshUser()]);
    setRefreshKey((k) => k + 1);
  }, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.autoSyncCheck()
      .then((result) => { if (result.synced) refreshAll(); })
      .catch(() => {}); // silent - never block the app over a background sync
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [activeTab, setActiveTab] = useState('overview');
  const [pendingTxnSearch, setPendingTxnSearch] = useState('');
  const [pendingTxnCategory, setPendingTxnCategory] = useState('');
  const [pendingTxnPreset, setPendingTxnPreset] = useState('this_month');
  const [pendingTxnMode, setPendingTxnMode] = useState('list');
  const [focusCategoryOverBudget, setFocusCategoryOverBudget] = useState(null);
  const [focusStuckPending, setFocusStuckPending] = useState(false);
  const [focusTransfersToReview, setFocusTransfersToReview] = useState(false);

  const goToTransactionsForMerchant = (merchant) => {
    setPendingTxnSearch(merchant);
    setPendingTxnCategory('');
    setPendingTxnPreset('all');
    setPendingTxnMode('list');
    setFocusStuckPending(false);
    setFocusTransfersToReview(false);
    setFocusCategoryOverBudget(null);
    setActiveTab('transactions');
  };

  const goToTransactionsForCategory = (category, period) => {
    setPendingTxnSearch('');
    setPendingTxnCategory(category);
    setPendingTxnPreset(period === 'week' ? 'this_week' : 'this_month');
    setPendingTxnMode('list');
    setFocusStuckPending(false);
    setFocusTransfersToReview(false);
    setFocusCategoryOverBudget(null);
    setActiveTab('transactions');
  };

  const goToIncomeTransactions = () => {
    setPendingTxnSearch('');
    setPendingTxnCategory('');
    setPendingTxnMode('income');
    setFocusStuckPending(false);
    setFocusTransfersToReview(false);
    setFocusCategoryOverBudget(null);
    setActiveTab('transactions');
  };

  const goToTopSpenders = () => {
    setPendingTxnSearch('');
    setPendingTxnCategory('');
    setPendingTxnMode('top');
    setFocusStuckPending(false);
    setFocusTransfersToReview(false);
    setFocusCategoryOverBudget(null);
    setActiveTab('transactions');
  };

  const goToBigPurchases = () => {
    setPendingTxnSearch('');
    setPendingTxnCategory('');
    setPendingTxnMode('big');
    setFocusStuckPending(false);
    setFocusTransfersToReview(false);
    setFocusCategoryOverBudget(null);
    setActiveTab('transactions');
  };

  // Needs Attention click-through: routes to the tab like before, plus
  // sets up whatever extra context that tab needs to land on the SPECIFIC
  // thing flagged (a category, a set of stuck transactions) instead of
  // just the top of a generic page.
  const handleNeedsAttentionNavigate = (item) => {
    setFocusCategoryOverBudget(null);
    setFocusStuckPending(false);
    setFocusTransfersToReview(false);
    if (item.id === 'over-budget-categories' && item.categories?.length) {
      const worst = [...item.categories].sort((a, b) => b.overage - a.overage)[0];
      setFocusCategoryOverBudget(worst);
    } else if (item.id === 'stuck-pending-transactions') {
      setFocusStuckPending(true);
    } else if (item.id === 'recent-transfers-to-review') {
      setFocusTransfersToReview(true);
    }
    setActiveTab(item.tab);
  };

  useEffect(() => {
    trackPageview(`/${activeTab}`);
    api.trackEvent('tab_view', activeTab).catch(() => {}); // fire-and-forget
  }, [activeTab]);

  const canSee = (tabId) => {
    if (user.role === 'owner') return true;
    if (!user.visibleTabs) return true; // null = unrestricted
    return user.visibleTabs.includes(tabId);
  };

  const tabs = [
    { id: 'overview', label: '◈ Overview' },
    { id: 'transactions', label: '🧾 Transactions' },
    { id: 'subscriptions', label: '🔄 Subscriptions' },
    { id: 'magi', label: '🏥 MAGI' },
    { id: 'investments', label: '💎 Investments' },
    { id: 'realestate', label: '🏘 Real Estate' },
    { id: 'ai', label: '🤖 AI' },
    { id: 'manage', label: '🛠 Manage' },
    { id: 'accounts', label: '🏦 Accounts' },
    ...(user.isAdmin ? [{ id: 'admin', label: '🛠 Admin' }] : []),
  ].filter((tab) => tab.id === 'admin' || canSee(tab.id));

  useEffect(() => {
    if (tabs.length > 0 && !tabs.find((t) => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  // Maps each tab to its sponsor placement - investments/admin have none
  const TAB_PLACEMENTS = {
    overview: 'overview',
    transactions: 'transactions',
    subscriptions: 'subscriptions',
    magi: 'magi',
    accounts: 'accounts',
  };

  if (error) {
    return (
      <div style={styles.errorPage}>
        <div style={styles.errorCard}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠</div>
          <h2 style={{ color: '#ef4444', margin: '0 0 0.5rem' }}>Connection Error</h2>
          <p style={{ color: '#9ca3af', margin: '0 0 1rem' }}>{error}</p>
          <button style={styles.retryBtn} onClick={refresh}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.app}>
      {/* Header */}
      <header style={styles.header} className="app-header">
        <div style={styles.headerLeft}>
          <span style={styles.logo}>
            <LogoMark size={26} />
            Boring Budgeting
          </span>
          <span style={styles.household}>{user.householdName}</span>
        </div>
        <div className="header-right" style={styles.headerRight}>
          {lastRefresh && (
            <span className="refresh-time" style={styles.refreshTime}>
              Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <FeedbackButton pageContext={activeTab} />
          <button
            style={{ ...styles.syncBtn, opacity: syncing ? 0.6 : 1 }}
            onClick={sync}
            disabled={syncing}
            className="sync-btn"
            title="Sync accounts"
          >
            <span aria-hidden="true">↻</span>
            <span className="btn-label">{syncing ? ' Syncing...' : ' Sync'}</span>
          </button>
          <button style={styles.logoutBtn} onClick={onLogout} className="logout-btn" title="Sign out">
            <span className="btn-label">Sign Out</span>
            <span className="logout-icon-mobile" aria-hidden="true">⏻</span>
          </button>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav style={styles.nav}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            style={{
              ...styles.navTab,
              ...(activeTab === tab.id ? styles.navTabActive : {}),
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <SponsorTicker placement={TAB_PLACEMENTS[activeTab]} />

      {/* Main Content */}
      <main style={styles.main}>
        {loading && !summary ? (
          <LoadingContent />
        ) : (
          <>
            {activeTab === 'overview' && (
              <>
                <SafeToSpendHero safeToSpend={summary?.safeToSpend} upcomingBills={summary?.upcomingBills} />
                <NeedsAttention onNavigate={handleNeedsAttentionNavigate} />
                <SpendingRecapCard />

                {/* Desktop: two independent columns, each sized by its own content */}
                <div className="desktop-og-grid" style={styles.grid}>
                  <div style={styles.col}>
                    <BudgetWidget
                      budget={summary?.budget}
                      annualBudget={summary?.annualBudget || user.annualBudget}
                    />
                    <BillsVsSpendable bills={summary?.estimatedMonthlyBills} budget={summary?.budget} />
                    <BigPurchasesBanner budget={summary?.budget} onViewAll={goToBigPurchases} />
                    <SpendingChart monthlySpend={monthlySpend} />
                    <SpendingBreakdownCard refreshKey={refreshKey} onCategoryClick={goToTransactionsForCategory} />
                    <IncomeTracker onViewIncome={goToIncomeTransactions} refreshKey={refreshKey} />
                    <TopSpendersSnapshot onViewAll={goToTopSpenders} onViewCharges={goToTransactionsForMerchant} refreshKey={refreshKey} />
                    <BigPurchasesSnapshot onViewAll={goToBigPurchases} onViewCharges={goToTransactionsForMerchant} refreshKey={refreshKey} />
                  </div>
                  <div style={styles.sideCol}>
                    {canSee('accounts') && (
                      <AccountsSidebar accounts={accounts} onRefresh={refreshAll} householdId={user.householdId} defaultCollapsed />
                    )}
                    <BudgetGlance budget={summary?.budget} />
                    {canSee('investments') && <InvestmentsSnapshot refreshKey={refreshKey} />}
                    {canSee('investments') && <TopTickers refreshKey={refreshKey} />}
                    {canSee('subscriptions') && (
                      <SubscriptionsSnapshot data={subscriptions} onViewAll={() => setActiveTab('subscriptions')} />
                    )}
                    <CategoryBudgetsSnapshot onViewAll={() => setActiveTab('manage')} refreshKey={refreshKey} />
                    {canSee('magi') && <MagiSnapshot data={magi} />}
                    <SponsorCard placement="overview" />
                  </div>
                </div>

                {/* Mobile: single stack, its own independent order */}
                <div className="mobile-og-stack">
                  <BudgetWidget
                    budget={summary?.budget}
                    annualBudget={summary?.annualBudget || user.annualBudget}
                  />
                  <BillsVsSpendable bills={summary?.estimatedMonthlyBills} budget={summary?.budget} />
                  <BigPurchasesBanner budget={summary?.budget} onViewAll={goToBigPurchases} />
                  <BudgetGlance budget={summary?.budget} />
                  <SpendingChart monthlySpend={monthlySpend} />
                  <SpendingBreakdownCard refreshKey={refreshKey} onCategoryClick={goToTransactionsForCategory} />
                  <IncomeTracker onViewIncome={goToIncomeTransactions} refreshKey={refreshKey} />
                  {canSee('investments') && <InvestmentsSnapshot refreshKey={refreshKey} />}
                  {canSee('investments') && <TopTickers refreshKey={refreshKey} />}
                  {canSee('subscriptions') && (
                    <SubscriptionsSnapshot data={subscriptions} onViewAll={() => setActiveTab('subscriptions')} />
                  )}
                  <CategoryBudgetsSnapshot onViewAll={() => setActiveTab('manage')} refreshKey={refreshKey} />
                  <TopSpendersSnapshot onViewAll={goToTopSpenders} onViewCharges={goToTransactionsForMerchant} refreshKey={refreshKey} />
                  <BigPurchasesSnapshot onViewAll={goToBigPurchases} onViewCharges={goToTransactionsForMerchant} refreshKey={refreshKey} />
                  {canSee('accounts') && (
                    <AccountsSidebar accounts={accounts} onRefresh={refreshAll} householdId={user.householdId} defaultCollapsed />
                  )}
                  {canSee('magi') && <MagiSnapshot data={magi} />}
                  <SponsorCard placement="overview" />
                </div>
              </>
            )}

            {activeTab === 'transactions' && (
              <>
                <SponsorCard placement="transactions" />
                {focusStuckPending ? (
                  <StuckPendingReview
                    onDismiss={() => setFocusStuckPending(false)}
                    onResolved={refreshAll}
                  />
                ) : focusTransfersToReview ? (
                  <TransferReviewList
                    onDismiss={() => setFocusTransfersToReview(false)}
                    onResolved={refreshAll}
                  />
                ) : (
                  <AllTransactions
                    onGoToManage={() => {
                      setActiveTab('manage');
                      setTimeout(() => {
                        document.getElementById('manual-transaction-entry')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                    initialSearch={pendingTxnSearch}
                    initialCategory={pendingTxnCategory}
                    initialPreset={pendingTxnSearch ? 'all' : pendingTxnPreset}
                    initialMode={pendingTxnMode}
                    onRefresh={refreshAll}
                    bigPurchaseThreshold={user.bigPurchaseThreshold}
                    bigPurchaseExcludedCategories={user.bigPurchaseExcludedCategories}
                    refreshKey={refreshKey}
                  />
                )}
              </>
            )}

            {activeTab === 'subscriptions' && (
              <>
                <SponsorCard placement="subscriptions" />
                <SubscriptionPanel data={subscriptions} onRefresh={refreshAll} onViewCharges={goToTransactionsForMerchant} />
              </>
            )}

            {activeTab === 'magi' && (
              <>
                <SponsorCard placement="magi" />
                <MagiTracker data={magi} onRefresh={refreshAll} />
              </>
            )}

            {activeTab === 'investments' && (
              <>
                <SponsorCard placement="investments" />
                <Investments refreshKey={refreshKey} onRefresh={refreshAll} />
              </>
            )}

            {activeTab === 'realestate' && <RealEstate />}

            {activeTab === 'ai' && <AIChat />}

            {activeTab === 'manage' && (
              <div style={styles.manageGrid}>
                <ManageGroupHeader icon="💰" title="Income & Cash Flow" accent="#00d4aa" />
                <HouseholdIncome user={user} onRefreshUser={refreshUser} onRefresh={refreshAll} defaultCollapsed={false} />

                <ManageGroupHeader icon="📐" title="Budget Setup" accent="#38bdf8" />
                <BudgetSettings user={user} onRefreshUser={refreshUser} onRefresh={refreshAll} defaultCollapsed />
                <BudgetCalculator defaultCollapsed user={user} refreshKey={refreshKey} />
                <CategoryBudgets
                  defaultCollapsed={!focusCategoryOverBudget}
                  refreshKey={refreshKey}
                  onRefresh={refreshAll}
                  highlightCategory={focusCategoryOverBudget?.category}
                />
                <FixedCostCategories defaultCollapsed onRefresh={refreshAll} />

                <ManageGroupHeader icon="🎯" title="Goals & Investing" accent="#a78bfa" />
                <InvestmentGoal defaultCollapsed refreshKey={refreshKey} onRefresh={refreshAll} />
                <SavingsGoals defaultCollapsed refreshKey={refreshKey} onRefresh={refreshAll} />

                <ManageGroupHeader icon="📋" title="Bills & Transactions" accent="#fbbf24" />
                <div id="manual-transaction-entry">
                  <RecentTransactions
                    transactions={summary?.recentTransactions}
                    onRefresh={refreshAll}
                    title="✏️ Manual Transaction Entry"
                    forceManualOnly
                    collapsible
                    defaultCollapsed={false}
                  />
                </div>
                <PlannedExpenses data={plannedExpenses} onRefresh={refreshAll} defaultCollapsed />

                <ManageGroupHeader icon="💳" title="Debt Freedom" accent="#ef4444" />
                <DebtPayoff defaultCollapsed refreshKey={refreshKey} onRefresh={refreshAll} />
              </div>
            )}

            {activeTab === 'accounts' && (
              <>
                <SponsorCard placement="accounts" />
                <AccountsSidebar accounts={accounts} onRefresh={refreshAll} householdId={user.householdId} />
                <HouseholdMembers user={user} />
                <SecuritySettings user={user} onRefreshUser={refreshUser} />
                <InstallPrompt />
              </>
            )}

            {activeTab === 'admin' && user.isAdmin && <AdminPanel />}
          </>
        )}
      </main>
      <CookieConsent />
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={styles.loadingScreen}>
      <div style={styles.loadingLogo}><LogoMark size={48} /></div>
      <div style={styles.loadingText}>Boring Budgeting</div>
    </div>
  );
}

function LoadingContent() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {[1, 2, 3].map((i) => (
        <div key={i} style={{
          height: 140,
          background: 'linear-gradient(90deg, #111827 25%, #1e2a3a 50%, #111827 75%)',
          borderRadius: '16px',
          border: '1px solid #1e2a3a',
        }} />
      ))}
    </div>
  );
}

const styles = {
  app: {
    minHeight: '100vh',
    background: '#0a0f1e',
    fontFamily: "'Syne', sans-serif",
    color: '#fff',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #1e2a3a',
    background: '#111827',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  logo: {
    fontSize: '0.95rem',
    fontWeight: '800',
    color: '#00d4aa',
    letterSpacing: '-0.02em',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    whiteSpace: 'nowrap',
  },
  household: {
    color: '#4b5563',
    fontSize: '0.8rem',
    paddingLeft: '0.75rem',
    borderLeft: '1px solid #1e2a3a',
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  refreshTime: {
    color: '#374151',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
  },
  syncBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#00d4aa',
    borderRadius: '6px',
    padding: '5px 12px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    fontWeight: '600',
  },
  logoutBtn: {
    background: 'transparent',
    border: 'none',
    color: '#4b5563',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontFamily: "'Syne', sans-serif",
  },
  nav: {
    display: 'flex',
    padding: '0 1.5rem',
    borderBottom: '1px solid #1e2a3a',
    background: '#111827',
    overflowX: 'auto',
    gap: '0',
  },
  navTab: {
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: '#6b7280',
    padding: '0.875rem 1rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
    transition: 'all 0.15s',
  },
  navTabActive: {
    color: '#00d4aa',
    borderBottomColor: '#00d4aa',
  },
  main: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1.5rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 340px',
    gap: '1rem',
    alignItems: 'start',
  },
  col: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  sideCol: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  manageGrid: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  manageGroupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginTop: '2rem',
    marginBottom: '0.4rem',
  },
  manageGroupIconBadge: {
    width: '36px',
    height: '36px',
    borderRadius: '11px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.15rem',
    flexShrink: 0,
  },
  manageGroupTitle: {
    color: '#f3f4f6',
    fontSize: '1.2rem',
    fontWeight: '800',
    fontFamily: "'Syne', sans-serif",
    letterSpacing: '-0.01em',
  },
  errorPage: {
    minHeight: '100vh',
    background: '#0a0f1e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorCard: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '2rem',
    textAlign: 'center',
    maxWidth: '400px',
  },
  retryBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontSize: '0.9rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  loadingScreen: {
    minHeight: '100vh',
    background: '#0a0f1e',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
  },
  loadingLogo: {
    fontSize: '3rem',
    color: '#00d4aa',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  loadingText: {
    fontSize: '1.15rem',
    fontWeight: '800',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
    letterSpacing: '-0.02em',
  },
};
